import {
    canBookGuidanceGeneralSession,
    canUpgradeForMoreGuidanceSessions,
    getGuidanceOnboardingOrder,
    getGuidanceSessionTypeDisplayName,
    getNextGuidanceOnboardingSessionType,
    hasCompletedGuidanceOnboarding,
} from '@fruitful/guidance';
import {
    type GuidanceSession,
    GuidanceSessionType,
    type SubscriptionProduct,
} from '@fruitful/types';
import type { ImageSourcePropType } from 'react-native';

import BuildingWealth from '../../../assets/images/guidance_sessions/building_wealth.png';
import Discovery from '../../../assets/images/guidance_sessions/discovery.png';
import Hello from '../../../assets/images/guidance_sessions/hello.png';
import Milestone from '../../../assets/images/guidance_sessions/milestone.png';
import QuickCheckin from '../../../assets/images/guidance_sessions/quick_checkin.png';
import Roadmap from '../../../assets/images/guidance_sessions/roadmap.png';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';

interface GuidanceSessionDetails {
    title: string;
    description: string;
    image: ImageSourcePropType;
}

// TODO: Move this to the @fruitful/guidance package when we can also move the images (or no longer need them)
const getSessionDetails = (
    sessionType: GuidanceSessionType,
    product: SubscriptionProduct | null | undefined,
): GuidanceSessionDetails => {
    const title = getGuidanceSessionTypeDisplayName(sessionType, product);

    switch (sessionType) {
        case GuidanceSessionType.DISCOVERY:
            return {
                title,
                // SEE ALSO a one-off description in OnboardingBookAvailableGuides.tsx -> SessionConfirmationSheet
                description: 'Bring your personalized money system to life.',
                image: Discovery,
            };
        case GuidanceSessionType.ROADMAP:
            return {
                title,
                description:
                    'Fine-tune your system and go even deeper on your goals.',
                image: Roadmap,
            };
        case GuidanceSessionType.BUILDING_WEALTH:
            return {
                title,
                description:
                    'Unpack how to invest smarter to build your ideal future.',
                image: BuildingWealth,
            };
        case GuidanceSessionType.QUICK_CHECK_IN:
            return {
                title,
                description:
                    'Connect with your Guide to adjust your money system as life changes.',
                image: QuickCheckin,
            };
        // --- Legacy Sessions below here (maintained for displaying member's guidance session history)
        case GuidanceSessionType.WELCOME_CALL:
            return {
                title,
                description: 'Get started on your journey and meet your Guide.',
                image: Hello,
            };
        case GuidanceSessionType.REVIEW_CAPSTONE:
            return {
                title,
                description:
                    'Check in on progress and make sure you feel confident about the future.',
                image: Milestone,
            };
        case GuidanceSessionType.MEMBER_CALL:
        case GuidanceSessionType.INVESTMENT_MANAGEMENT:
        default:
            return {
                title,
                description:
                    'A member call is a chance to discuss your progress and goals with your Guide.',
                image: QuickCheckin,
            };
    }
};

export const useMembershipGuidanceSessionInfo = (): {
    guidanceOnboardingOrder: GuidanceSessionType[];
    getSessionDetails: (type: GuidanceSessionType) => GuidanceSessionDetails;
    getNextGuidanceOnboardingSessionType: (
        pastSessions: GuidanceSession[],
    ) => GuidanceSessionType | null;
    hasCompletedGuidanceOnboarding: (
        pastSessions: GuidanceSession[],
    ) => boolean;
    canBookGuidanceGeneralSession: (pastSessions: GuidanceSession[]) => boolean;
    canUpgradeForMoreGuidanceSessions: (
        pastSessions: GuidanceSession[],
    ) => boolean;
} => {
    const product = useAuthedUser().membership?.product ?? null;

    return {
        guidanceOnboardingOrder: getGuidanceOnboardingOrder(product),
        getSessionDetails: (type: GuidanceSessionType) =>
            getSessionDetails(type, product),
        getNextGuidanceOnboardingSessionType: (
            pastSessions: GuidanceSession[],
        ) => getNextGuidanceOnboardingSessionType(pastSessions, product),
        hasCompletedGuidanceOnboarding: (pastSessions: GuidanceSession[]) =>
            hasCompletedGuidanceOnboarding(pastSessions, product),
        canBookGuidanceGeneralSession: (pastSessions: GuidanceSession[]) =>
            canBookGuidanceGeneralSession(pastSessions, product),
        canUpgradeForMoreGuidanceSessions: (pastSessions: GuidanceSession[]) =>
            canUpgradeForMoreGuidanceSessions(product, pastSessions),
    };
};
